/**
 * Locale-aware formatting (ADR-0009/D-5, owner decision A5). Thin layer over `Intl` so every screen formats numbers,
 * money and dates the same way:
 * - digits: Latin (0-9) by default in both languages, Arabic-Indic (٠-٩) selectable;
 * - calendar: always Gregorian for business dates; Hijri (Umm al-Qura) only as an optional secondary display;
 * - business dates (`YYYY-MM-DD`) never shift with the viewer's time zone; instants are shown in the branch zone;
 * - one date display everywhere (IN-I-21, IN-S-23): by default a date reads year-month-day (`2026-10-31`, in the chosen
 *   digits) in both languages — as the date pickers, lists and grids show it — instead of a locale pattern that read
 *   `31/10/2026` in Arabic and `Oct 31, 2026` in English next to `2026-10-31` in the same page. `long` stays available
 *   for running text ("31 October 2026").
 */

export type DigitShape = "latn" | "arab";
export type NegativeStyle = "minus" | "parentheses";
/** `numeric`: year-month-day as the date pickers show it (default); the others are the `Intl` date styles. */
export type DateStyle = "numeric" | "short" | "medium" | "long";

/** A decimal value as returned by the API (string keeps full precision) or a JS number. */
export type DecimalInput = number | bigint | string;

export interface FormatSettings {
  /** UI language (`ar` | `en`). */
  language: string;
  /** Region from tenant settings, e.g. `JO`, `SA`. Optional. */
  region?: string;
  /** Digit shape; Latin by default (A5). */
  digits?: DigitShape;
  /** How negative amounts are written (legacy GridAmountsMaskID / ReportAmountsMaskID). */
  negativeStyle?: NegativeStyle;
  /** IANA time zone used for instants (branch zone, P8). Defaults to the browser zone. */
  timeZone?: string;
}

export interface CurrencyOptions {
  /** Minor units of the currency (ADR-0004), e.g. 3 for JOD, 2 for USD. Defaults to the ISO 4217 value from Intl. */
  minorUnits?: number;
  /** `code` (JOD) or `symbol` (د.أ.). Defaults to `symbol`. */
  display?: "code" | "symbol" | "narrowSymbol" | "name";
  negativeStyle?: NegativeStyle;
}

export interface NumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
  negativeStyle?: NegativeStyle;
}

const businessDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/u;

/** Builds a BCP 47 locale with explicit numbering system and calendar extensions. */
export function buildLocale(
  settings: Pick<FormatSettings, "language" | "region" | "digits">,
  calendar: "gregory" | "islamic-umalqura" = "gregory",
): string {
  const base = settings.region ? `${settings.language}-${settings.region}` : settings.language;
  return `${base}-u-ca-${calendar}-nu-${settings.digits ?? "latn"}`;
}

/** True for a complete, real business date (`YYYY-MM-DD`), false for text still being typed or an impossible date. */
export function isBusinessDate(value: string): boolean {
  const match = businessDatePattern.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  );
}

/** Parses a business date (`YYYY-MM-DD`) as a UTC midnight instant so it never shifts across time zones. */
export function parseBusinessDate(value: string): Date {
  const match = businessDatePattern.exec(value);
  if (!match) {
    throw new RangeError(`Not a business date (YYYY-MM-DD): ${value}`);
  }
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

/** Removes the bidi marks Intl inserts so the sign can be repositioned safely. */
function stripBidiMarks(text: string): string {
  return text.replace(/[\u200e\u200f\u061c]/gu, "");
}

function isNegative(value: DecimalInput): boolean {
  if (typeof value === "string") return value.trim().startsWith("-") && !/^-0*(?:\.0*)?$/u.test(value.trim());
  return value < 0;
}

function absolute(value: DecimalInput): DecimalInput {
  if (typeof value === "string") return value.trim().replace(/^-/u, "");
  if (typeof value === "bigint") return value < 0n ? -value : value;
  return Math.abs(value);
}

function formatWithSign(format: Intl.NumberFormat, value: DecimalInput, style: NegativeStyle): string {
  // `Intl.NumberFormat#format` accepts decimal strings exactly (ES2023); typings only list number | bigint.
  const run = (input: DecimalInput) => format.format(input as number);
  if (style === "parentheses" && isNegative(value)) {
    return `(${stripBidiMarks(run(absolute(value)))})`;
  }
  return run(value);
}

export interface Formatter {
  readonly locale: string;
  readonly settings: Readonly<FormatSettings>;
  /** Plain number with grouping. */
  number(value: DecimalInput, options?: NumberOptions): string;
  /** Fixed number of decimals (quantities with a scale, rates). */
  decimal(
    value: DecimalInput,
    scale: number,
    options?: Omit<NumberOptions, "minimumFractionDigits" | "maximumFractionDigits">,
  ): string;
  /** Money with the currency's minor units. */
  currency(value: DecimalInput, currencyCode: string, options?: CurrencyOptions): string;
  /** Percentage of a ratio (0.15 → 15%). */
  percent(ratio: DecimalInput, fractionDigits?: number): string;
  /** Business date (`YYYY-MM-DD` or Date), Gregorian; text that is not a valid date yet is returned as is. */
  date(value: string | Date, style?: DateStyle): string;
  /** Instant (ISO string or Date) in the configured time zone. */
  dateTime(value: string | Date, style?: DateStyle): string;
  /** Time of an instant in the configured time zone. */
  time(value: string | Date): string;
  /** Optional Hijri (Umm al-Qura) display of a business date; never used for storage or input. */
  hijriDate(value: string | Date, style?: DateStyle): string;
}

/** ISO text that starts with a complete date; anything shorter (a date still being typed) is not a date yet. */
const isoPrefix = /^\d{4}-\d{2}-\d{2}(?:$|T)/u;

function toInstant(value: string | Date): Date {
  if (typeof value !== "string") return value;
  return isoPrefix.test(value) ? new Date(value) : new Date(Number.NaN);
}

function toBusinessDate(value: string | Date): Date {
  return typeof value === "string" && businessDatePattern.test(value) ? parseBusinessDate(value) : toInstant(value);
}

/**
 * Formats a date, or returns the input unchanged when it is not a valid date yet (for example a date still being
 * typed, `2026-1`), instead of letting `Intl` throw "Invalid time value" and break the page.
 */
function safeDate(format: Intl.DateTimeFormat, value: string | Date, toDate: (value: string | Date) => Date): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return typeof value === "string" ? value : "";
  return format.format(date);
}

/** Two-digit (four for the year) parts of a date in the locale's digits, joined year-month-day. */
function numericDate(locale: string, date: Date, timeZone: string | undefined): string {
  const parts = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  const text = `${part("year")}-${part("month")}-${part("day")}`;
  // Latin digits around "-" read left to right in an Arabic sentence; Arabic-Indic digits need an explicit LTR isolate
  // (the bidi algorithm does not join them across "-"), else the day would come first.
  return /^[0-9-]+$/u.test(text) ? text : `\u2066${text}\u2069`;
}

function safeNumeric(
  locale: string,
  value: string | Date,
  toDate: (value: string | Date) => Date,
  timeZone: string | undefined,
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return typeof value === "string" ? value : "";
  return numericDate(locale, date, timeZone);
}

/** Creates a formatter for the given settings. Cheap; cache per settings change in UI code. */
export function createFormatter(settings: FormatSettings): Formatter {
  const locale = buildLocale(settings);
  const hijriLocale = buildLocale(settings, "islamic-umalqura");
  const defaultNegative = settings.negativeStyle ?? "minus";
  const zone = settings.timeZone ? { timeZone: settings.timeZone } : {};

  return {
    locale,
    settings: { ...settings },
    number(value, options = {}) {
      const { negativeStyle = defaultNegative, ...rest } = options;
      return formatWithSign(new Intl.NumberFormat(locale, rest), value, negativeStyle);
    },
    decimal(value, scale, options = {}) {
      const { negativeStyle = defaultNegative, ...rest } = options;
      const format = new Intl.NumberFormat(locale, {
        ...rest,
        minimumFractionDigits: scale,
        maximumFractionDigits: scale,
      });
      return formatWithSign(format, value, negativeStyle);
    },
    currency(value, currencyCode, options = {}) {
      const { minorUnits, display = "symbol", negativeStyle = defaultNegative } = options;
      const format = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: display,
        ...(minorUnits === undefined ? {} : { minimumFractionDigits: minorUnits, maximumFractionDigits: minorUnits }),
      });
      return formatWithSign(format, value, negativeStyle);
    },
    percent(ratio, fractionDigits = 0) {
      const format = new Intl.NumberFormat(locale, {
        style: "percent",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      return formatWithSign(format, ratio, defaultNegative);
    },
    date(value, style = "numeric") {
      if (style === "numeric") return safeNumeric(locale, value, toBusinessDate, "UTC");
      return safeDate(new Intl.DateTimeFormat(locale, { dateStyle: style, timeZone: "UTC" }), value, toBusinessDate);
    },
    dateTime(value, style = "numeric") {
      if (style === "numeric") {
        const date = safeNumeric(locale, value, toInstant, settings.timeZone);
        if (date === value || date === "") return date;
        return `${date} ${new Intl.DateTimeFormat(locale, { timeStyle: "short", ...zone }).format(toInstant(value))}`;
      }
      return safeDate(
        new Intl.DateTimeFormat(locale, { dateStyle: style, timeStyle: "short", ...zone }),
        value,
        toInstant,
      );
    },
    time(value) {
      return safeDate(new Intl.DateTimeFormat(locale, { timeStyle: "short", ...zone }), value, toInstant);
    },
    hijriDate(value, style = "medium") {
      if (style === "numeric") style = "short";
      return safeDate(
        new Intl.DateTimeFormat(hijriLocale, { dateStyle: style, timeZone: "UTC" }),
        value,
        toBusinessDate,
      );
    },
  };
}
